import ChargeHelper, { ChargeConstants } from 'features/Charge/chargeHelper';
import KTFormSearch from 'general/components/OtherKeenComponents/Forms/KTFormSearch';
import KTPagination, {
    KTPaginationColor,
} from 'general/components/OtherKeenComponents/KTPagination';
import KTTable from 'general/components/OtherKeenComponents/KTTable';
import tableStyle from 'general/components/OtherKeenComponents/KTTable/tableStyle';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import AppColors from 'general/constants/AppColors';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import useConfigTableColumn from 'hooks/useConfigTableColumn';
import useRouter from 'hooks/useRouter';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import AppResource from 'general/constants/AppResource';
import AppRoute from 'general/constants/AppRoute';
import Global from 'general/utils/Global';
import KTTooltip from 'general/components/OtherKeenComponents/KTTooltip';
import { AppCustomDialogHelper } from 'general/components/AppDialogCustom';
import DialogChargeInventoryAddView from '../DialogChargeInventoryAddView';
import PropTypes from 'prop-types';
import DialogInventoryAddView from '../DialogInventoryAddNew';

ChargeInventoryListView.propTypes = {
    stationId: PropTypes.string,
    showFullUI: PropTypes.bool,
};

const defaultColumnIds = [
    'IMAGE',
    'CODE',
    'NAME',
    'SLUG',
    'PRODUCT_CATEGORY',
    'BRAND',
    'DISPLAY_ORDER',
    'VISIBLE',
    'ORIGINAL_PRICE',
    'PRICE',
    'COMMISSION',
    'QUANTITY',
    'UNIT',
    'SHORT_DESCRIPTION',
    'DESCRIPTION',
];

const parseSpecs = (row) => {
    let specs = row?.technicalSpecs || {};
    if (_.isString(specs)) {
        try {
            specs = JSON.parse(specs);
        } catch (error) {
            specs = {};
        }
    }
    return specs || {};
};

const normalizeProductImage = (value) => {
    if (!value || value === 'null' || value === 'undefined') return '';

    let image = value;
    if (_.isString(image)) {
        const rawImage = image.trim();
        try {
            image = JSON.parse(rawImage);
        } catch (error) {
            image = rawImage.replace(/^"+|"+$/g, '');
        }
    }

    if (_.isArray(image)) {
        image = image.find(Boolean);
    }

    if (image && _.isObject(image)) {
        image = image.url || image.path || image.relativePath || image.src || '';
    }

    if (!image || image === 'null' || image === 'undefined') return '';
    const cleanImage = String(image).replace(/^"+|"+$/g, '');
    if (
        cleanImage.startsWith('/static/')
        || cleanImage.startsWith('static/')
        || cleanImage.startsWith('data:image')
        || cleanImage.startsWith('blob:')
    ) {
        return cleanImage.startsWith('static/') ? `/${cleanImage}` : cleanImage;
    }
    return Utils.getFullUrl(cleanImage);
};

const ProductImageView = ({ image, size = 44 }) => {
    const [hasError, setHasError] = useState(false);
    const imageUrl = normalizeProductImage(image);
    return (
        <div
            className="d-flex align-items-center justify-content-center"
            style={{
                width: size,
                height: size,
                borderRadius: 6,
                overflow: 'hidden',
                border: `1px solid ${AppColors.gray[300]}`,
                background: 'linear-gradient(135deg, #ecfdf5, #ffffff)',
            }}
        >
            {imageUrl && !hasError ? (
                <img
                    src={imageUrl}
                    alt="product"
                    onError={() => setHasError(true)}
                    style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
            ) : (
                <span style={{ color: AppColors.primary, fontWeight: 900, fontSize: size > 80 ? 28 : 13 }}>
                    EVG
                </span>
            )}
        </div>
    );
};

const ProductDetailView = ({ row }) => {
    const specs = parseSpecs(row);
    const rows = [
        ['Mã sản phẩm', specs.productCode || row?.categoryCode || `SP${row?.categoryId?.toString().padStart(8, '0')}`],
        ['Tên sản phẩm', row?.name],
        ['Danh mục sản phẩm', row?.productCategory?.name],
        ['Giá gốc', row?.originalPrice ? `${Utils.formatNumber(row.originalPrice)} VNĐ` : '-'],
        ['Giá bán', row?.price ? `${Utils.formatNumber(row.price)} VNĐ` : 'Liên hệ'],
        ['Hoa hồng affiliate', `${row?.commissionRate || 0}%`],
        ['Đặc điểm', [specs.category, specs.chargingLevel, specs.mountType].filter(Boolean)],
        ['Kích thước', specs.dimensions],
        ['Đầu vào', specs.input],
        ['Xuất xứ', specs.origin],
        ['Điện áp đầu vào', specs.voltage],
        ['Phương pháp sạc', specs.chargeMethod],
        ['Kết nối', specs.connection],
        ['Công suất đầu ra tối đa', specs.maxOutput],
        ['Công suất phích cắm đơn', specs.socketOutput],
        ['Nhiệt độ hoạt động', specs.temperature],
        ['Độ ẩm', specs.humidity],
        ['Giao thức kết nối', specs.protocol],
        ['Trọng lượng', specs.weight],
        ['Mức độ bảo vệ', specs.protection],
        ['Đặc điểm chi tiết', specs.features],
        ['Lưu ý', specs.note],
        ['Mô tả ngắn', specs.shortDescription ? <div dangerouslySetInnerHTML={{ __html: specs.shortDescription }} /> : '-'],
        ['Mô tả', row?.description],
    ];

    return (
        <div className="p-4">
            <div className="d-flex gap-4 mb-4">
                <ProductImageView image={row?.images} size={128} />
                <div className="d-flex flex-column justify-content-center">
                    <div style={UIHelper.buildFontStyle('800', '22px', AppColors.gray[900])}>
                        {row?.name}
                    </div>
                    {row?.originalPrice ? (
                        <div className="mt-2" style={{ ...UIHelper.buildFontStyle('600', '14px', AppColors.danger), textDecoration: 'line-through' }}>
                            {Utils.formatNumber(row.originalPrice)} VNĐ
                        </div>
                    ) : null}
                    <div className="mt-2" style={UIHelper.buildFontStyle('700', '18px', AppColors.primary)}>
                        {row?.price ? `${Utils.formatNumber(row.price)} VNĐ` : 'Liên hệ'}
                    </div>
                    <div className="mt-2" style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[600])}>
                        Hoa hồng affiliate: {row?.commissionRate || 0}%
                    </div>
                </div>
            </div>
            <div className="table-responsive">
                <table className="table table-bordered mb-0">
                    <tbody>
                        {rows.map(([label, value]) => (
                            <tr key={label}>
                                <td style={{ width: 240, fontWeight: 700 }}>{label}</td>
                                <td>
                                    {_.isArray(value) ? (
                                        <ul className="mb-0 pl-4">
                                            {value.filter(Boolean).map((item) => <li key={item}>{item}</li>)}
                                        </ul>
                                    ) : (
                                        value || '-'
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

function ChargeInventoryListView({ stationId, chargeStationDetail = {}, showFullUI = true }) {
    // MARK: ---- PROPERTIES ----
    const router = useRouter();
    const { user } = useSelector((state) => state.auth);
    const isAdmin = user?.role === 'ADMIN' || user?.role === 'OPERATOR';
    const prefKey = useMemo(() => {
        const userId = user.userId;
        const pathName = router.pathname;
        const key = `${userId}_${pathName}`;
        return key;
    }, [user, router]);
    const isMine = useMemo(() => {
        return chargeStationDetail?.agencyId === user?.agencyId || chargeStationDetail?.landlordId === user?.agencyId;
    }, [chargeStationDetail])
    const [filter, setFilter] = useState(Global.g_filterChargeInventoryList);
    const [chargeInventoryList, setChargeInventoryList] = useState([]);
    const [total, setTotal] = useState(0);
    const [orderChanged, setOrderChanged] = useState(false);
    useEffect(() => {
        let params = _.pickBy(filter, (value) => value !== null && value !== undefined && value !== '');
        if (!stationId) {
            if (user?.role === ChargeConstants.UserRole.AGENCY && Utils.isValid(user?.agencyId)) {
                params.agencyId = user?.agencyId;
            }
            ChargeHelper.API.requestGetChargeInventoryCategoryList(params)
                .then((response) => {
                    setChargeInventoryList(response?.categories);
                    setTotal(response?.total);
                    setOrderChanged(false);
                })
                .finally(() => { });
        } else {
            params.stationId = stationId;
            if (user?.role === ChargeConstants.UserRole.AGENCY && Utils.isValid(user?.agencyId)) {
                params.agencyId = user?.agencyId;
            }
            ChargeHelper.API.requestGetChargeInventoryList(params)
                .then((response) => {
                    setChargeInventoryList(response?.items);
                    setTotal(response?.total);
                    setOrderChanged(false);
                })
                .finally(() => { });
        }
    }, [filter, user, stationId]);
    const fullColumns = useMemo(() => {
        return [
            {
                id: 'IMAGE',
                name: UIHelper.buildTableColumn('Ảnh'),
                selector: (row) => <ProductImageView image={row?.images} />,
                width: '70px',
            },
            {
                id: 'CODE',
                name: UIHelper.buildTableColumn('Mã sản phẩm'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => handleShowDetail(row)}
                    >
                        {!stationId
                            ? row?.categoryCode ?? `SP${row?.categoryId?.toString().padStart(8, '0')}`
                            : row?.inventoryCode ?? `SP${row?.inventoryId?.toString().padStart(8, '0')}`}
                    </span>
                ),
                minWidth: '160px',
                style: tableStyle.columns.link,
            },
            {
                id: 'NAME',
                name: UIHelper.buildTableColumn('Tên sản phẩm'),
                selector: (row) => (
                    <span
                        className="cursor-pointer hover-opacity-60"
                        onClick={() => handleShowDetail(row)}
                    >
                        {row?.name ?? ''}
                    </span>
                ),
                minWidth: '220px',
                style: tableStyle.columns.link,
            },
            {
                id: 'SLUG',
                name: UIHelper.buildTableColumn('Đường dẫn SEO (Slug)'),
                selector: (row) => row?.slug || '-',
                minWidth: '200px',
                style: tableStyle.columns.normal,
            },
            {
                id: !stationId ? 'PRODUCT_CATEGORY' : '',
                name: UIHelper.buildTableColumn('Danh mục sản phẩm'),
                selector: (row) => row?.productCategory?.name || '-',
                minWidth: '180px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'BRAND',
                name: UIHelper.buildTableColumn('Hãng sản xuất'),
                selector: (row) => (
                    <span className="cursor-pointer hover-opacity-60" onClick={() => {
                        Utils.openInNewTab(`${AppRoute.manageChargeBrand}/${!stationId ? row?.brandId ?? '' : row?.category?.chargeBrand?.brandId ?? ''}`)
                    }}>
                        {!stationId ? row?.chargeBrand?.name ?? '' : row?.category?.chargeBrand?.name ?? ''}
                    </span>
                ),
                minWidth: '220px',
                style: tableStyle.columns.link,
            },
            {
                id: !stationId ? 'DISPLAY_ORDER' : '',
                name: UIHelper.buildTableColumn('Thứ tự KH'),
                selector: (row) => Utils.formatNumber(row?.displayOrder || 0),
                minWidth: '110px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: !stationId ? 'VISIBLE' : '',
                name: UIHelper.buildTableColumn('Hiển thị KH'),
                selector: (row) => row?.isVisible !== false ? 'Có' : 'Ẩn',
                minWidth: '120px',
                style: tableStyle.columns.normal,
            },
            {
                id: isAdmin ? 'PRICE' : '',
                name: UIHelper.buildTableColumn('Đơn giá', 'VNĐ'),
                selector: (row) => row?.price ? Utils.formatNumber(row?.price) : '',
                minWidth: '160px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: isAdmin ? 'ORIGINAL_PRICE' : '',
                name: UIHelper.buildTableColumn('Giá gốc', 'VNĐ'),
                selector: (row) => row?.originalPrice ? Utils.formatNumber(row?.originalPrice) : '',
                minWidth: '160px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: isAdmin ? 'COMMISSION' : '',
                name: UIHelper.buildTableColumn('Hoa hồng', '%'),
                selector: (row) => row?.commissionRate ? Utils.formatNumber(row?.commissionRate) : '0',
                minWidth: '130px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: stationId ? 'QUANTITY' : '',
                name: UIHelper.buildTableColumn('Số lượng'),
                selector: (row) => row?.quantity ? Utils.formatNumber(row?.quantity) : '',
                minWidth: '160px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: 'UNIT',
                name: UIHelper.buildTableColumn('Đơn vị tính'),
                selector: (row) => !stationId ? row?.unit ?? '' : row?.category?.unit ?? '',
                minWidth: '120px',
                style: tableStyle.columns.normal,
                right: true,
            },
            {
                id: 'DESCRIPTION',
                name: UIHelper.buildTableColumn('Mô tả'),
                selector: (row) => {
                    return (
                        <KTTooltip text={row?.description} isHtmlContent={true}>
                            <div 
                                className="my-2 text-ellipsis-2-lines" 
                                style={{ whiteSpace: 'normal' }}
                                dangerouslySetInnerHTML={{ __html: row?.description }}
                            />
                        </KTTooltip>
                    );
                },
                minWidth: '400px',
                style: tableStyle.columns.normal,
            },
            {
                id: 'SHORT_DESCRIPTION',
                name: UIHelper.buildTableColumn('Mô tả ngắn'),
                selector: (row) => {
                    const specs = parseSpecs(row);
                    const shortDesc = specs.shortDescription || '';
                    return (
                        <div 
                            className="my-2" 
                            style={{ whiteSpace: 'normal', maxHeight: 80, overflow: 'hidden' }}
                            dangerouslySetInnerHTML={{ __html: shortDesc }}
                        />
                    );
                },
                minWidth: '260px',
                style: tableStyle.columns.normal,
            },
            {
                name: UIHelper.buildTableColumn('Thao tác'),
                minWidth: '160px',
                style: {
                    position: 'sticky',
                    right: 0,
                    backgroundColor: '#fff',
                    borderLeft: '1px solid #ebedfe',
                },
                cell: (row) => {
                    return UIHelper.buildMoreActionsButton(buildMoreActions(row), true, false, 4);
                },
                omit: !isAdmin && !isMine
            },
        ];
    }, [user, isAdmin, isMine]);
    const [fullColumnIds, setFullColumnIds] = useState(
        fullColumns?.map((row) => row?.id).filter(Boolean) || []
    );
    const {
        columnIds,
        columns,
        onChangeCheckboxValue,
        resetDefaultTableColumn,
        onSelectAll,
        onUnSelectAll,
        fullColumnsSorted,
        fullColumnsIdSorted,
        saveChanges,
    } = useConfigTableColumn(prefKey, fullColumnIds, defaultColumnIds, fullColumns);
    const [selectedChargeBrand, setSelectedChargeBrand] = useState(null);
    const [selectedProductCategory, setSelectedProductCategory] = useState(null);
    const dropdownChargeBrandEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="brandId"
                name="brandId"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                        type="button"
                    >
                        {selectedChargeBrand?.name ?? 'Chọn hãng sản xuất'}
                    </button>
                }
                selectedValue={selectedChargeBrand}
                dropdownMenuItems={[{ name: 'Hãng sản xuất', role: 'CHARGE_BRAND' }]}
                getSubItems={async (item) => {
                    return ChargeHelper.API.requestGetChargeBrandList({
                        page: item?.page || 0,
                        limit: 10,
                        q: item?.q || '',
                    }).then((data) => {
                        return {
                            values: data.brands?.map((row) => ({
                                id: row?.brandId,
                                name: row?.name,
                                item: row,
                            })),
                            total: data.total,
                        };
                    });
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedChargeBrand(value);
                    Global.g_filterChargeInventoryList = {
                        ...Global.g_filterChargeInventoryList,
                        brandId: value?.id,
                        page: 0,
                    };
                    handleRefresh();
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Hãng sản xuất',
                    role: 'CHARGE_BRAND',
                }}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{
                            borderTop: `1px solid ${AppColors.gray[300]}`,
                        }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            setSelectedChargeBrand(null);
                            Global.g_filterChargeInventoryList = {
                                ...Global.g_filterChargeInventoryList,
                                brandId: '',
                                modelId: '',
                                page: 0,
                            };
                            handleRefresh();
                        })}
                    </div>
                }
            />
        );
    }, [selectedChargeBrand]);

    const dropdownProductCategoryEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="productCategoryId"
                name="productCategoryId"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between`}
                        type="button"
                    >
                        {selectedProductCategory?.name ?? 'Chọn danh mục sản phẩm'}
                    </button>
                }
                selectedValue={selectedProductCategory}
                dropdownMenuItems={[{ name: 'Danh mục sản phẩm', role: 'PRODUCT_CATEGORY' }]}
                getSubItems={async (item) => {
                    return ChargeHelper.API.requestGetProductCategoryList({
                        page: item?.page || 0,
                        limit: 10,
                        q: item?.q || '',
                    }).then((data) => ({
                        values: data.categories?.map((row) => ({
                            id: row?.productCategoryId,
                            name: row?.name,
                            item: row,
                        })),
                        total: data.total,
                    }));
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedProductCategory(value);
                    Global.g_filterChargeInventoryList = {
                        ...Global.g_filterChargeInventoryList,
                        productCategoryId: value?.id,
                        page: 0,
                    };
                    handleRefresh();
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Danh mục sản phẩm',
                    role: 'PRODUCT_CATEGORY',
                }}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{ borderTop: `1px solid ${AppColors.gray[300]}` }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            setSelectedProductCategory(null);
                            Global.g_filterChargeInventoryList = {
                                ...Global.g_filterChargeInventoryList,
                                productCategoryId: '',
                                page: 0,
                            };
                            handleRefresh();
                        })}
                    </div>
                }
            />
        );
    }, [selectedProductCategory]);


    // MARK: ---- METHODS ----
    const buildMoreActions = (row) => {
        const actions = [];
        if (isAdmin || isMine) {
            // chinh sua
            actions.push({
                text: 'Chỉnh sửa',
                iconElement: <i className="fa-regular fa-edit" style={{ fontSize: '1.1rem' }} />,
                click: () => {
                    if (!stationId) {
                        handleAddNew(row);
                    } else {
                        handleAddInventory(row);
                    }
                },
            });
            // xoa
            if (!stationId) {
                actions.push({
                    text: 'Đưa lên trên',
                    iconElement: <i className="fa-solid fa-arrow-up" style={{ fontSize: '1.1rem' }} />,
                    click: () => {
                        handleMoveProduct(row, -1);
                    },
                });
                actions.push({
                    text: 'Đưa xuống dưới',
                    iconElement: <i className="fa-solid fa-arrow-down" style={{ fontSize: '1.1rem' }} />,
                    click: () => {
                        handleMoveProduct(row, 1);
                    },
                });
                actions.push({
                    text: 'Xóa sản phẩm',
                    iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
                    click: () => {
                        handleDelete(row);
                    },
                });
            } else {
                actions.push({
                    text: 'Xóa thiết bị',
                    iconElement: <i className="fa-regular fa-trash-can" style={{ fontSize: '1.1rem' }} />,
                    click: () => {
                        handleDeleteInventory(row);
                    },
                });
            }
        }
        return actions;
    };

    const handleRefresh = () => {
        setFilter({ ...Global.g_filterChargeInventoryList });
    };
    const handleMoveProduct = (row, direction) => {
        setChargeInventoryList((current) => {
            const currentList = current || [];
            const index = currentList.findIndex((item) => item.categoryId === row.categoryId);
            const nextIndex = index + direction;
            if (index < 0 || nextIndex < 0 || nextIndex >= currentList.length) return currentList;
            const next = [...currentList];
            const [target] = next.splice(index, 1);
            next.splice(nextIndex, 0, target);
            setOrderChanged(true);
            return next.map((item, itemIndex) => ({
                ...item,
                displayOrder: itemIndex + filter.page * filter.limit + 1,
            }));
        });
    };
    const handleSaveProductOrder = async () => {
        const result = await ChargeHelper.API.requestReorderChargeInventoryCategories(
            (chargeInventoryList || []).map((item, index) => ({
                categoryId: item.categoryId,
                displayOrder: index + filter.page * filter.limit + 1,
            }))
        );
        if (result === true) {
            ToastHelper.showSuccess('Đã lưu thứ tự hiển thị sản phẩm');
            handleRefresh();
        } else {
            ToastHelper.showError(result || 'Lưu thứ tự thất bại');
        }
    };
    const handleDelete = (row) => {
        ChargeHelper.UI.Inventory.showDialogConfirmDeleteCategory(row, () => {
            handleRefresh();
        });
    };
    const handleDeleteInventory = (row) => {
        ChargeHelper.UI.Inventory.showDialogConfirmDeleteInventory(row, () => {
            handleRefresh();
        });
    };
    const handleAddNew = (row) => {
        AppCustomDialogHelper.show(
            !!row ? 'Sửa sản phẩm' : 'Thêm sản phẩm',
            <DialogChargeInventoryAddView
                item={row}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'xl',
            false,
            true,
            true,
            true,
            'custom-modal-xxl',
            false
        );
    };
    const handleShowDetail = (row) => {
        AppCustomDialogHelper.show(
            'Chi tiết sản phẩm',
            <ProductDetailView row={row} />,
            'xl',
            false,
            true,
            false // canClickOutsideToDismiss: false means dismissible
        );
    };
    const handleAddInventory = (row) => {
        AppCustomDialogHelper.show(
            !!row ? 'Sửa thiết bị khác' : 'Thêm thiết bị khác',
            <DialogInventoryAddView
                item={row}
                stationId={stationId}
                onCancel={() => AppCustomDialogHelper.hide()}
                onDone={() => {
                    AppCustomDialogHelper.hide();
                    handleRefresh();
                }}
            />,
            'lg',
            false,
            true
        );
    };

    return (
        <div className={`${!showFullUI ? 'px-4' : ''}`}>
            {UIHelper.buildCard(
                <div className="pb-4">
                    {/* header */}
                    {showFullUI && (
                        <div className="px-4 pt-4 pb-3 d-flex align-items-center justify-content-between">
                            {/* title */}
                            <div className="d-flex flex-column">
                                <span style={UIHelper.buildFontStyle('600', '18px', AppColors.gray[800])}>
                                    Trang thiết bị & dịch vụ
                                </span>
                                <span style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[800])}>
                                    Tổng:{' '}
                                    <span style={UIHelper.buildFontStyle('600', '14px', AppColors.primary)}>
                                        {Utils.formatNumber(total)} bản ghi
                                    </span>
                                </span>
                            </div>

                            {/* actions */}
                            <div className="d-flex align-items-center gap-2">
                                {UIHelper.buildGreenButton(
                                    'Thêm sản phẩm',
                                    handleAddNew,
                                    <i className="fa-regular fa-circle-plus" />
                                )}
                                {!stationId && orderChanged && UIHelper.buildGreenButton(
                                    'Lưu thứ tự',
                                    handleSaveProductOrder,
                                    <i className="fa-solid fa-floppy-disk" />
                                )}
                            </div>
                        </div>
                    )}

                    {/* body */}
                    <div className={`${!showFullUI ? 'pt-4' : ''}`}>
                        {/* filter */}
                        <div className="row m-0 p-0 px-2">
                            {/* tim kiem */}
                            <div className="col-3 mb-4 px-2">
                                {UIHelper.buildFormLabel('Tìm kiếm')}
                                <KTFormSearch
                                    name="search"
                                    placeholder="Theo tên sản phẩm, mã sản phẩm"
                                    value={filter.q}
                                    onSubmit={(text) => {
                                        Global.g_filterChargeInventoryList = {
                                            ...Global.g_filterChargeInventoryList,
                                            q: text,
                                            page: 0,
                                        };
                                        handleRefresh();
                                    }}
                                />
                            </div>
                            {/* loc theo hang san xuat */}
                            <div className="col-3 mb-4 px-2">
                                {UIHelper.buildFormLabel('Hãng sản xuất')}
                                {dropdownChargeBrandEl}
                            </div>
                            <div className="col-3 mb-4 px-2">
                                {!stationId && (
                                    <>
                                        {UIHelper.buildFormLabel('Danh mục sản phẩm')}
                                        {dropdownProductCategoryEl}
                                    </>
                                )}
                            </div>
                            <div className="col-3 mb-4 px-2 d-flex align-items-end justify-content-end">
                                {isAdmin && stationId && UIHelper.buildGreenButton(
                                    'Thêm thiết bị',
                                    handleAddInventory,
                                    <i className="fa-regular fa-circle-plus" />
                                )}
                            </div>
                        </div>

                        {/* table */}
                        <div className="px-4">
                            <KTTable
                                columns={columns}
                                items={chargeInventoryList}
                                selectableRows={false}
                                striped={false}
                                compactGrid={false}
                                hasActionColumn={isAdmin || isMine}
                                onClickedRow={(row) => { }}
                                onSelectedRowsChanged={(rows) => { }}
                                onDoubleClickedRow={(row) => { }}
                                noDataComponent={UIHelper.buildEmptyNoData(
                                    'Vui lòng kiểm tra kết nối mạng hoặc tải lại trang.'
                                )}
                            />
                        </div>

                        {/* pagination */}
                        <div className="px-4 mt-2">
                            <KTPagination
                                currentPage={filter.page + 1}
                                rowCount={total}
                                rowsPerPage={filter.limit}
                                currentItemInPageCount={chargeInventoryList?.length}
                                onChangePage={(newPage) => {
                                    const iNewPage = parseInt(newPage);
                                    Global.g_filterChargeInventoryList = {
                                        ...Global.g_filterChargeInventoryList,
                                        page: iNewPage - 1,
                                    };
                                    handleRefresh();
                                }}
                                onChangeRowsPerPage={(newRowsPerPage) => {
                                    const iNewRowsPerPage = parseInt(newRowsPerPage);
                                    Global.g_filterChargeInventoryList = {
                                        ...Global.g_filterChargeInventoryList,
                                        limit: iNewRowsPerPage,
                                        page: 0,
                                    };
                                    handleRefresh();
                                }}
                                loading={false}
                                color={KTPaginationColor.success}
                                appendText="bản ghi"
                            />
                        </div>
                    </div>
                </div>,
                false,
                false,
                true,
                false,
                AppColors.gray[300],
                !showFullUI
            )}
        </div>
    );
}

export default ChargeInventoryListView;
